import {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {Box, Typography} from '@mui/material';

import {ProfileComparisonProfileCard} from './ProfileComparisonProfileCard';
import {ProfileComparisonSearchBar} from './ProfileComparisonSearchBar';
import {
  profileComparisonOverflowSx,
  profileComparisonSectionCardSx,
  profileComparisonSectionTitleSx,
} from './profileComparisonSectionStyles';
import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {EmptyStateCard} from '../../../../../components/EmptyStateCard/EmptyStateCard';
import {Loading} from '../../../../../components/Loading/Loading';
import type {CompanyWorkforceProfile} from '../../types/WorkforceProfileTypes';
import type {ProfileComparisonFilterState} from '../../utils/ProfileComparison/filterProfileComparisonProfiles';
import {PROFILE_COMPARISON_GRID_LIMIT} from '../../utils/ProfileComparison/profileComparisonConstants';

export interface Props {
  profiles: CompanyWorkforceProfile[];
  profileAId: string | null;
  profileBId: string | null;
  isLoading: boolean;
  filters: ProfileComparisonFilterState;
  seniorityOptions: string[];
  departmentName: string;
  hasActiveFilters: boolean;
  onFiltersChange: (filters: ProfileComparisonFilterState) => void;
  onClearFilters: () => void;
  onProfileClick: (profileId: string) => void;
}

export const ProfileComparisonProfileGrid = ({
  profiles,
  profileAId,
  profileBId,
  isLoading,
  filters,
  seniorityOptions,
  departmentName,
  hasActiveFilters,
  onFiltersChange,
  onClearFilters,
  onProfileClick,
}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDepartmentHealthPage.profileComparison.grid'});

  const visibleProfiles = useMemo(() => profiles.slice(0, PROFILE_COMPARISON_GRID_LIMIT), [profiles]);
  const hasOverflow = profiles.length > PROFILE_COMPARISON_GRID_LIMIT;

  const pickHint = useMemo(() => {
    const selectedCount = (profileAId ? 1 : 0) + (profileBId ? 1 : 0);
    if (selectedCount === 0) {
      return t('pickHintNone');
    }
    if (selectedCount === 1) {
      return t('pickHintOne');
    }
    return t('pickHintBoth');
  }, [profileAId, profileBId, t]);

  return (
    <EmpionPaper sx={profileComparisonSectionCardSx} data-testid='profile-comparison-profile-grid'>
      <Box sx={{...profileComparisonSectionTitleSx, justifyContent: 'space-between', mb: 1.25}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
          <GridViewOutlinedIcon sx={{fontSize: 15, color: 'text.secondary'}} />
          {t('title')}
        </Box>
        <Typography sx={{fontSize: '12px', fontWeight: 400, color: 'text.secondary'}}>{pickHint}</Typography>
      </Box>

      <ProfileComparisonSearchBar
        filters={filters}
        seniorityOptions={seniorityOptions}
        departmentName={departmentName}
        profileCount={profiles.length}
        hasActiveFilters={hasActiveFilters}
        onFiltersChange={onFiltersChange}
        onClear={onClearFilters}
      />

      {isLoading ? (
        <Box sx={{py: 4, display: 'flex', justifyContent: 'center'}}>
          <Loading />
        </Box>
      ) : profiles.length === 0 ? (
        <EmptyStateCard message={String(t('empty'))} />
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
              gap: 1,
            }}
          >
            {visibleProfiles.map((profile) => {
              const selection =
                profile.resourceId === profileAId ? 'a' : profile.resourceId === profileBId ? 'b' : null;

              return (
                <ProfileComparisonProfileCard
                  key={profile.resourceId}
                  profile={profile}
                  selection={selection}
                  onClick={() => onProfileClick(profile.resourceId)}
                />
              );
            })}
          </Box>
          {hasOverflow && (
            <Box sx={profileComparisonOverflowSx}>
              <InfoOutlinedIcon sx={{fontSize: 16, color: 'text.secondary', mt: 0.125, flexShrink: 0}} />
              <Typography sx={{fontSize: '12px', color: 'text.secondary', lineHeight: 1.5}}>
                {t('overflowShort')}
              </Typography>
            </Box>
          )}
        </>
      )}
    </EmpionPaper>
  );
};
